import { useEffect } from 'react';
import { useMaybeAppData } from '@modules/root';
import {
    getTrackingWindow,
    injectHeadScript,
} from '../util/inject-head-script';

interface GTMProps {
    id: string;
}

export const GTMHead: React.FC<GTMProps> = ({ id }) => {
    // `useMaybeAppData` (not `useAppData`) because this also renders inside error
    // boundaries, where root data may be absent.
    const appData = useMaybeAppData();
    const user = appData?.user;
    // user_id for GA4 (aggregates signed-in users); anonymous_user_id for GrowthBook.
    const userID = user?.__typename === 'Me' ? user?.ID : undefined;
    const apUserID = user?.ID
        ? user.ID * (user?.__typename === 'Me' ? 1 : -1)
        : 0;

    // The container is injected after hydration (see `injectHeadScript`); the standard
    // GTM snippet would `insertBefore` gtm.js during parse and break document hydration.
    // The user ids are pushed before the container loads so they precede the first auto
    // page_view; `gtm.start` is seeded only on the initial inject.
    useEffect(() => {
        const w = getTrackingWindow();
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ userID, apUserID });
        if (
            injectHeadScript(`https://www.googletagmanager.com/gtm.js?id=${id}`)
        ) {
            w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
        }
    }, [id, userID, apUserID]);

    return null;
};

export const GTMBody: React.FC<GTMProps> = ({ id }) => {
    return (
        <noscript>
            <iframe
                src={`https://www.googletagmanager.com/ns.html?id=${id}`}
                height="0"
                width="0"
                style={{ display: 'none', visibility: 'hidden' }}
                title="gtm-tracker"
            />
        </noscript>
    );
};
